import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { withReactQuery } from '@/react-tools/withReactQuery';
import { withCurrentUser } from '@/react-tools/withCurrentUser';
import { useHelmRepoVersions } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmRepoVersions';
import { withUIRouter } from '@/react-tools/withUIRouter';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { HelmRelease } from '@/react/kubernetes/helm/types';
import { useHelmRegistryRepoOptions } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmRegistries';
import { useStack } from '@/react/common/stacks/queries/useStack';
import { createWebhookId } from '@/portainer/helpers/webhookHelper';
import {
  KubernetesStackPayload,
  useCreateKubernetesStackMutation,
} from '@/react/kubernetes/stacks/queries/useCreateStackMutation';
import { useUpdateGitStackSettings } from '@/react/portainer/gitops/queries/useUpdateGitStackSettings';
import { useUpdateGitStack } from '@/react/portainer/gitops/queries/useUpdateGitStack';
import {
  transformAutoUpdateViewModel,
  parseAutoUpdateResponse,
} from '@/react/portainer/gitops/AutoUpdateFieldset/utils';
import {
  AutoUpdateResponse,
  GitAuthenticationResponse,
} from '@/react/portainer/gitops/types';
import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { useUpdateHelmReleaseMutation } from '@/react/kubernetes/helm/helmReleaseQueries/useUpdateHelmReleaseMutation';
import { useDeleteStackMutation } from '@/react/common/stacks/queries/useDeleteStackMutation';
import { queryKeys } from '@/react/kubernetes/helm/helmReleaseQueries/query-keys';

import { openModal } from '@@/modals';
import { confirm } from '@@/modals/confirm';

import { UpgradeHelmInnerForm } from './UpgradeHelmInnerForm';
import { UpgradeHelmFormValues } from './types';
import { useChartReferenceState } from './utils/useChartReferenceState';
import { validation } from './validation';

interface Props {
  onSubmit: () => void;
  release: HelmRelease;
}

export function UpgradeHelmModal({
  release,
  onSubmit: handleCloseModal,
}: Props) {
  const environmentId = useEnvironmentId();
  const queryClient = useQueryClient();
  const [previewIsValid, setPreviewIsValid] = useState(false);

  // Fetch stack data if release is Git-managed
  const stackQuery = useStack(release.stackID, {
    enabled: !!release.stackID,
    retry: 1,
  });

  // Mutations for different upgrade scenarios
  const createStackMutation = useCreateKubernetesStackMutation(environmentId);
  const updateStackMutation = useUpdateGitStackSettings();
  const redeployStackMutation = useUpdateGitStack(
    release.stackID || 0,
    environmentId
  );
  const deleteStackMutation = useDeleteStackMutation();

  const updateHelmReleaseMutation = useUpdateHelmReleaseMutation(
    environmentId,
    { name: release.name, namespace: release.namespace }
  );

  const defaultVersionQuery = useHelmRepoVersions(
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      select: (versions) => versions?.[0],
    },
    release.chartReference,
    true,
    false
  );

  const registryOptionsQuery = useHelmRegistryRepoOptions(
    environmentId,
    release.namespace
  );
  const chartReferenceState = useChartReferenceState(
    release,
    registryOptionsQuery.registryOptions
  );

  const stack = stackQuery?.data;
  const gitConfig = stack?.GitConfig;
  const autoUpdate = stack?.AutoUpdate;
  const hasStackError = !!release.stackID && stackQuery.isError;

  const initialValues: UpgradeHelmFormValues = useMemo(
    () =>
      buildUpgradeInitialValues({
        release,
        defaultVersion: defaultVersionQuery.data?.version,
        selectedRegistry: chartReferenceState.selectedRegistry,
        selectedChartName: chartReferenceState.selectedChartName,
        userSuppliedValues: release.values?.userSuppliedValues,
        hasStackError,
        gitConfig,
        helmChartPath:
          stack?.HelmChartPath === './' ? undefined : stack?.HelmChartPath,
        helmValuesFiles: stack?.HelmValuesFiles,
        autoUpdate,
      }),
    // intentionally initialize the form once to avoid resets on background updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const isCheckingChartReference =
    chartReferenceState.source.isChecking ||
    chartReferenceState.chart.isChecking ||
    registryOptionsQuery.isLoading;

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnMount
      onSubmit={handleSubmit}
      validationSchema={validation}
    >
      <UpgradeHelmInnerForm
        release={release}
        onDismiss={handleCloseModal}
        chartReferenceInfoMessage={chartReferenceState.infoAlert}
        isCheckingChartReference={isCheckingChartReference}
        initialRegistryValue={chartReferenceState.selectedRegistry}
        chartReferenceState={{
          requiresSourceConfirmation:
            chartReferenceState.requiresSourceConfirmation,
          requiresChartConfirmation:
            chartReferenceState.requiresChartConfirmation,
        }}
        setPreviewIsValid={setPreviewIsValid}
        hasStackError={hasStackError}
      />
    </Formik>
  );

  async function handleSubmit(values: UpgradeHelmFormValues) {
    const wasGitManaged = !!release.stackID && !hasStackError;

    // Git → Git: update existing stack
    if (wasGitManaged && values.useGitOps) {
      await handleUpdateGitStack(values);
      handleCloseModal();
      return;
    }

    // Manual → Git: create new stack
    if (!wasGitManaged && values.useGitOps) {
      await handleCreateGitStack(values);
      handleCloseModal();
      return;
    }

    // Manual → Manual or Git → Manual: upgrade manually
    await handleManualUpgrade(values);
    handleCloseModal();
  }

  async function handleManualUpgrade(values: UpgradeHelmFormValues) {
    const wasGitManaged = !!release.stackID && !hasStackError;

    if (!previewIsValid) {
      const confirmed = await confirm({
        title: 'Chart validation failed',
        message:
          'The Helm manifest preview validation failed, which may indicate configuration issues. This can be normal when creating new resources. Do you want to proceed with the upgrade?',
      });
      if (!confirmed) {
        return;
      }
    }

    // If switching from Git to Manual, confirm the change
    if (wasGitManaged) {
      const confirmed = await confirm({
        title: 'Switch to manual mode',
        message:
          'This will remove Git management for this release. The stack will be deleted and you will manage updates manually. Continue?',
      });
      if (!confirmed) {
        return;
      }
    }

    // Optimistically update the query cache to show pending state
    const queryKey = queryKeys.release(
      environmentId,
      release.namespace || '',
      release.name,
      release.version,
      true
    );
    queryClient.setQueryData(queryKey, {
      ...release,
      info: {
        ...release.info,
        status: 'pending-upgrade',
        details: 'Upgrade in progress...',
        description: 'Upgrade in progress...',
      },
    });

    // Start the upgrade in the background
    updateHelmReleaseMutation.mutate({
      name: release.name,
      namespace: release.namespace,
      values: values.values,
      chart: values.chart,
      repo: values.repo,
      registryId: values.registryId,
      version: values.version,
      atomic: values.atomic,
    });

    // Delete the stack if switching from Git to Manual
    if (wasGitManaged && release.stackID && stack?.Name && release.namespace) {
      deleteStackMutation.mutate(
        {
          stackId: release.stackID,
          stackName: stack.Name,
          environmentId,
          namespace: release.namespace,
        },
        {
          onError: (error) => {
            notifyError(
              'Failed to delete Git stack',
              error as Error,
              'The Helm release was upgraded but the Git stack could not be removed'
            );
          },
        }
      );
    }

    notifySuccess('Success', 'Helm chart upgrade started');
  }

  async function handleCreateGitStack(values: UpgradeHelmFormValues) {
    if (!previewIsValid) {
      const confirmed = await confirm({
        title: 'Chart validation failed',
        message:
          'The Helm manifest preview validation failed. Do you want to proceed with switching to Git mode?',
      });
      if (!confirmed) {
        return;
      }
    }

    const confirmed = await confirm({
      title: 'Switch to Git mode',
      message:
        'Switching to Git mode will manage this release from the repository. Current manual values will be replaced. Continue?',
    });
    if (!confirmed) {
      return;
    }

    const payload: KubernetesStackPayload = {
      method: 'repository',
      type: 'kubernetes',
      StackName: release.name,
      ComposeFormat: false,
      Namespace: release.namespace || '',
      RepositoryURL: values.RepositoryURL || '',
      RepositoryReferenceName: values.RepositoryReferenceName || '',
      RepositoryAuthentication: values.RepositoryAuthentication || false,
      RepositoryUsername: values.RepositoryUsername || '',
      RepositoryPassword: values.RepositoryPassword || '',
      RepositoryAuthorizationType: values.RepositoryAuthorizationType || 0,
      RepositoryGitCredentialID: values.RepositoryGitCredentialID || 0,
      ManifestFile: '',
      HelmChartPath: values.HelmChartPath || './',
      AdditionalFiles: [],
      HelmValuesFiles: values.HelmValuesFiles || [],
      TLSSkipVerify: values.TLSSkipVerify || false,
      AutoUpdate: transformAutoUpdateViewModel(
        values.AutoUpdate,
        values.webhookId
      ),
    };

    await createStackMutation.mutateAsync(payload, {
      onSuccess() {
        notifySuccess(
          'Success',
          'Helm release switched to Git management successfully'
        );
        handleCloseModal();
      },
    });
  }

  async function handleUpdateGitStack(values: UpgradeHelmFormValues) {
    if (!release.stackID) {
      return;
    }

    if (!previewIsValid) {
      const confirmed = await confirm({
        title: 'Chart validation failed',
        message:
          'The Helm manifest preview validation failed. Do you want to proceed with the update?',
      });
      if (!confirmed) {
        return;
      }
    }

    const basePayload = {
      env: [],
      RepositoryReferenceName: values.RepositoryReferenceName,
      RepositoryAuthentication: values.RepositoryAuthentication,
      RepositoryGitCredentialID: values.RepositoryGitCredentialID,
      RepositoryUsername: values.RepositoryUsername,
      RepositoryPassword: values.RepositoryPassword,
      RepositoryAuthorizationType: values.RepositoryAuthorizationType,
      TLSSkipVerify: values.TLSSkipVerify,
      HelmChartPath: values.HelmChartPath,
      HelmValuesFiles: values.HelmValuesFiles,
      Atomic: values.atomic,
      AutoUpdate: transformAutoUpdateViewModel(
        values.AutoUpdate,
        values.webhookId
      ),
    };

    // First, update the stack settings
    await updateStackMutation.mutateAsync({
      stackId: release.stackID,
      endpointId: environmentId,
      payload: basePayload,
    });

    // Detect if Git configuration has changed (chart path, values files, or git reference)
    const hasGitConfigChanged =
      stack?.HelmChartPath !== values.HelmChartPath ||
      stack?.GitConfig?.ReferenceName !== values.RepositoryReferenceName ||
      JSON.stringify(stack?.HelmValuesFiles) !==
        JSON.stringify(values.HelmValuesFiles);

    // When automatic updates are disabled, always trigger an immediate upgrade
    // since there is no polling to pick up the changes.
    // Also trigger redeploy if Git config changed (chart path, values files, or reference)
    // to apply changes immediately instead of waiting for next Git commit.
    const isAutoUpdateDisabled =
      !values.AutoUpdate || !values.AutoUpdate.RepositoryAutomaticUpdates;
    const shouldRedeploy =
      values.redeployNow || isAutoUpdateDisabled || hasGitConfigChanged;

    // If redeployNow is checked, automatic updates are disabled, or git config changed, optimistically update and close modal
    if (shouldRedeploy) {
      // Optimistically update the query cache to show pending state
      const queryKey = queryKeys.release(
        environmentId,
        release.namespace || '',
        release.name,
        release.version,
        true
      );
      queryClient.setQueryData(queryKey, {
        ...release,
        info: {
          ...release.info,
          status: 'pending-upgrade',
          details: 'Upgrade in progress...',
          description: 'Upgrade in progress...',
        },
      });

      notifySuccess(
        'Success',
        'Git configuration updated. Upgrading Helm release...'
      );

      // Trigger redeploy in the background
      redeployStackMutation.mutate(basePayload);
    } else {
      notifySuccess('Success', 'Git configuration updated successfully');
    }
  }
}

export async function openUpgradeHelmModal(release: HelmRelease) {
  return openModal(
    withUIRouter(withReactQuery(withCurrentUser(UpgradeHelmModal))),
    {
      release,
    }
  );
}

function buildUpgradeInitialValues(args: {
  release: HelmRelease;
  defaultVersion?: string;
  selectedRegistry?: { registryUrl?: string; registryId?: number };
  selectedChartName?: string;
  userSuppliedValues?: string;
  hasStackError: boolean;
  gitConfig?: {
    URL?: string;
    ReferenceName?: string;
    TLSSkipVerify?: boolean;
    Authentication?: GitAuthenticationResponse;
  };
  helmChartPath?: string;
  helmValuesFiles?: string[];
  autoUpdate?: AutoUpdateResponse | undefined;
}): UpgradeHelmFormValues {
  const {
    release,
    defaultVersion,
    selectedRegistry,
    selectedChartName,
    userSuppliedValues,
    hasStackError,
    gitConfig,
    helmChartPath,
    helmValuesFiles,
    autoUpdate,
  } = args;

  return {
    // Manual mode fields
    version: release.chart.metadata?.version ?? defaultVersion ?? '',
    repo: selectedRegistry?.registryUrl,
    registryId: selectedRegistry?.registryId,
    chart: selectedChartName ?? '',
    values: userSuppliedValues ?? '',
    atomic: true,

    // Git mode toggle
    useGitOps: !!release.stackID && !hasStackError,

    // Git configuration fields
    RepositoryURL: gitConfig?.URL || '',
    RepositoryURLValid: !!gitConfig?.URL,
    RepositoryReferenceName: gitConfig?.ReferenceName || '',
    TLSSkipVerify: gitConfig?.TLSSkipVerify || false,
    RepositoryAuthentication: !!gitConfig?.Authentication,
    RepositoryUsername: gitConfig?.Authentication?.Username || '',
    RepositoryPassword: '',
    RepositoryGitCredentialID: gitConfig?.Authentication?.GitCredentialID || 0,
    RepositoryAuthorizationType:
      gitConfig?.Authentication?.AuthorizationType ?? 0,
    NewCredentialName: '',
    SaveCredential: false,

    // Helm-specific Git fields
    HelmChartPath: helmChartPath || '',
    HelmValuesFiles: helmValuesFiles || [],

    // Auto update configuration
    AutoUpdate: parseAutoUpdateResponse(autoUpdate),
    webhookId: autoUpdate?.Webhook || createWebhookId(),

    // Redeploy trigger (default true)
    redeployNow: true,
  };
}
